<template>
  <div>
    <project-toolbar
      :selectMonth="true"
      :selectProject="true"
      :project="accountProject"
      :showCreditFilter="true"
      ref="projectToolbar"
    >
      <md-field class="md-search-field" md-inline>
        <label for="sku" class="sku-search-label">{{ L('skuName') }}</label>
        <md-select
          v-model="selectedSkuIds"
          name="sku"
          multiple
          md-dense
          md-inline
          :disabled="!objectUsageData[preMonth]"
        >
          <div class="sku-search-div">
            <div class="md-layout">
              <search-field
                v-model="searchSku"
                :fields="searchFields"
                class="md-layout-item md-size-80"
              />
              <div class="md-layout-item md-size-5"></div>
              <md-button
                @click="setApplyedSkuIds"
                class="sku-apply-button md-layout-item md-size-15 md-layout md-alignment-center-right md-raised md-primary"
                >{{ L('apply') }}</md-button
              >
            </div>
            <md-button
              class="md-raised sku-select-button md-layout-item md-size-50"
              @click="setAllSkuIds"
              >All</md-button
            >
            <md-button
              class="md-raised sku-select-button md-layout-item md-size-50"
              @click="setClearSkuIds"
              >Clear</md-button
            >
          </div>
          <div class="search-space-div"></div>
          <div style="max-width: 400px">
            <md-option
              v-for="skuId in searchedSkuIds"
              :key="skuId"
              :value="skuId"
            >
              {{ skuNames[skuId] }}
            </md-option>
          </div>
        </md-select>
      </md-field>
    </project-toolbar>
    <md-content v-if="chartData.length > 0">
      <md-card class="md-layout" v-if="curProjectId === totalProjectId">
        <md-card-header>
          <span class="card-title">{{ L('costByProject') }} (Top 10) </span>
        </md-card-header>
        <md-card-content class="md-layout-item md-size-100">
          <GChart
            type="ColumnChart"
            :data="projectChartData"
            :options="projectChartOptions"
          />
        </md-card-content>
      </md-card>
      <md-card class="md-layout">
        <md-card-header class="card-title md-layout">
          <span class="card-title">{{ L('costBySku') }} (Top 10)</span>
          <div class="md-layout md-alignment-top-right">
            <span class="md-switch">
              {{ L('columnChart') }}
            </span>
            <md-switch v-model="isLineChart"> {{ L('lineChart') }} </md-switch>
          </div>
        </md-card-header>
        <md-card-content class="md-layout-item md-size-100">
          <GChart
            :type="commonChartType"
            :data="commonChartData"
            :options="commonChartOptions"
          />
        </md-card-content>
      </md-card>
      <div class="md-layout">
        <div
          class="md-layout-item md-size-35 md-medium-size-100 md-small-size-100 md-xsmall-size-100"
        >
          <md-card>
            <md-card-header class="card-title md-layout">
              <span class="card-title">{{ L('costBySku') }} (Top 15)</span>
              <div class="md-layout md-alignment-top-right">
                <span>({{ preMonth }})</span>
              </div>
            </md-card-header>
            <md-card-content class="md-layout-item donut-chart-height">
              <GChart
                type="PieChart"
                :data="donutChartData"
                :options="donutChartOptions"
                style="height: 100%"
              />
            </md-card-content>
          </md-card>
        </div>
        <div
          class="md-layout-item md-size-65 md-medium-size-100 md-small-size-100 md-xsmall-size-100 cost-table"
        >
          <md-card>
            <md-card-header>
              <span class="card-title">{{ L('costBySku') }} (All)</span>
            </md-card-header>
            <md-card-content class="table-height">
              <md-table
                v-model="tableChartData.list"
                class="main-table min-table"
                md-fixed-header
                md-thin
                md-stripe
              >
                <md-table-row slot="md-table-row" slot-scope="{ item }">
                  <md-table-cell
                    :md-label="L('product')"
                    style="width: 40%"
                    md-sort-by="skuName"
                    >{{ item.skuName }}</md-table-cell
                  >
                  <md-table-cell
                    :md-label="item.threeMonthAgo.month"
                    md-sort-by="costThreeMonthAgo"
                    md-numeric
                    >{{
                      item.threeMonthAgo.cost | formatCurrency(displayCurrency)
                    }}</md-table-cell
                  >
                  <md-table-cell
                    :md-label="item.twoMonthAgo.month"
                    md-sort-by="costTwoMonthAgo"
                    md-numeric
                    >{{
                      item.twoMonthAgo.cost | formatCurrency(displayCurrency)
                    }}</md-table-cell
                  >
                  <md-table-cell
                    :md-label="item.oneMonthAgo.month"
                    md-sort-by="costOneMonthAgo"
                    md-numeric
                    >{{
                      item.oneMonthAgo.cost | formatCurrency(displayCurrency)
                    }}</md-table-cell
                  >
                  <md-table-cell
                    :md-label="L('variance')"
                    md-sort-by="variance"
                    md-numeric
                    >{{ item.variance }} %</md-table-cell
                  >
                </md-table-row>
              </md-table>
              <md-table class="total-table" ref="totalTable" md-thin>
                <md-table-row slot="md-table-row">
                  <md-table-cell style="width: 40%">{{
                    L('totalCost')
                  }}</md-table-cell>
                  <md-table-cell md-numeric>{{
                    tableChartData.total.costThreeMonthAgo
                      | formatCurrency(displayCurrency)
                  }}</md-table-cell>
                  <md-table-cell md-numeric>{{
                    tableChartData.total.costTwoMonthAgo
                      | formatCurrency(displayCurrency)
                  }}</md-table-cell>
                  <md-table-cell md-numeric>{{
                    tableChartData.total.costOneMonthAgo
                      | formatCurrency(displayCurrency)
                  }}</md-table-cell>
                  <md-table-cell style="padding-right: 8px" md-numeric
                    >{{ tableChartData.total.variance }} %</md-table-cell
                  >
                </md-table-row>
              </md-table>
            </md-card-content>
          </md-card>
        </div>
      </div>
    </md-content>
    <md-content v-else class="md-layout md-alignment-center-center">
      <p style="text-align: center; margin-top: 100px">{{ L('noData') }}</p>
    </md-content>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { GChart } from 'vue-google-charts'
import api from '@/api'
import { hasProperty, hasNestedProperties } from '@/lib/utils'
import { getProject } from '@/lib/aggutil'
import { formatDate, getMonthBefore } from '@/lib/dateutil'
import SearchField from '@/views/layout/SearchField'
import ProjectToolbar from '@/views/billing/ProjectToolbar'

export default {
  components: {
    GChart,
    ProjectToolbar,
    SearchField,
  },

  data() {
    return {
      accountProject: {
        all: false,
        list: [],
      },
      totalProjectId: '__total',

      searchFields: {
        sku_name: 'sku',
      },

      searchSku: '',
      selectedSkuIds: [],
      applyedSkuIds: {
        curProject: [],
        otherProject: [],
      },

      usageDataPeriod: 12,
      projectChartPeriod: 12,
      commonChartPeriod: 12,

      projectChartCount: 10,
      donutChartCount: 15,

      usageData: [],
      objectUsageData: {},
      chartData: [],

      projectNames: {},
      skuNames: {},

      isLineChart: true,

      projectChartOptions: {
        legend: { position: 'top', maxLines: 4 },
        isStacked: 'true',
        height: 450,
      },
      columnChartOptions: {
        legend: { position: 'top', maxLines: 4 },
        isStacked: 'true',
        height: 450,
      },
      lineChartOptions: {
        legend: { position: 'top', maxLines: 4 },
        curveType: 'function',
        height: 450,
        vAxis: {
          viewWindow: {
            min: 0,
          },
        },
      },
      donutChartOptions: {
        chartArea: {
          width: '80%',
          height: '80%',
          top: 7,
          bottom: 7,
          left: 30,
          right: 30,
        },
        pieHole: 0.4,
        legend: {
          alignment: 'center',
        },
        sliceVisibilityThreshold: 0.0,
      },
    }
  },

  watch: {
    curAccount() {
      this.load()
    },
    curMonth() {
      this.load()
    },
    curProjectId() {
      this.initApplyedSkuIds()
    },
    enabledCredit() {
      this.objectUsageData = this.getObjectUsageData()
      this.initApplyedSkuIds()
    },
  },

  computed: {
    ...mapState({
      curAccount: (state) => state.curAccount,
      curMonth: (state) => state.curMonth,
      curProject: (state) => state.curProject,
      preMonth: (state) => state.preMonth,
      userInfo: (state) => state.userInfo,
      displayCurrency: (state) => state.displayCurrency,
      creditFilter: (state) => state.creditFilter,
      exchangeRateList: (state) => state.exchangeRateList,
    }),

    curProjectId() {
      return this.curProject.id
    },
    curAccountId() {
      return this.curAccount.id
    },

    skuIds() {
      let usageDataByProject
      if (this.curProjectId === this.totalProjectId) {
        usageDataByProject = this.usageData
      } else if (this.curProjectId === '__account') {
        usageDataByProject = this.usageData.filter(
          (data) => data.project_id === ''
        )
      } else {
        usageDataByProject = this.usageData.filter(
          (data) => data.project_id === this.curProjectId
        )
      }

      return usageDataByProject.reduce((arr, usageData) => {
        if (!arr.includes(usageData.sku_id)) {
          arr.push(usageData.sku_id)
        }
        return arr
      }, [])
    },
    searchedSkuIds() {
      const skuIds = this.applyedSkuIds.curProject.reduce((arr, skuId) => {
        if (!arr.includes(skuId)) {
          arr.push(skuId)
        }
        return arr
      }, this.skuIds)
      return [...skuIds, ...this.applyedSkuIds.otherProject].filter((skuId) =>
        this.skuNames[skuId]
          .toLowerCase()
          .includes(this.searchSku.trim().toLowerCase())
      )
    },

    commonChartType() {
      return this.isLineChart ? 'LineChart' : 'ColumnChart'
    },
    commonChartOptions() {
      return this.isLineChart ? this.lineChartOptions : this.columnChartOptions
    },

    projectChartData() {
      const lastMonthByUsageData = Object.keys(this.objectUsageData)[0]
      const projectIds = Object.keys(this.projectNames)
        .map((projectId) => ({
          projectId,
          cost: this.chartData
            .filter((skuId) =>
              hasNestedProperties(this.objectUsageData[lastMonthByUsageData], [
                projectId,
                skuId,
              ])
            )
            .reduce(
              (totalCost, skuId) =>
                totalCost +
                this.getCostByCurrency(
                  this.objectUsageData[lastMonthByUsageData][projectId][skuId]
                ),
              0
            ),
        }))
        .sort((project1, project2) => project2.cost - project1.cost)
        .slice(0, this.projectChartCount)
        .map(({ projectId }) => projectId)

      const chartHeader = [
        'Month',
        ...projectIds.map((projectId) =>
          projectId === '__account'
            ? `(${this.L('account')})`
            : this.projectNames[projectId]
        ),
      ]
      const chartBody = this.getMonths(this.projectChartPeriod).map((month) => {
        if (!hasProperty(this.objectUsageData, month)) {
          return [month, ...projectIds.map(() => 0)]
        }
        const projectCosts = projectIds.map((projectId) =>
          this.chartData.reduce((cost, skuId) => {
            if (
              hasNestedProperties(this.objectUsageData[month], [
                projectId,
                skuId,
              ])
            ) {
              return (
                cost +
                this.getCostByCurrency(
                  this.objectUsageData[month][projectId][skuId]
                )
              )
            }
            return cost
          }, 0)
        )

        return [month, ...projectCosts]
      })

      return [chartHeader, ...chartBody]
    },

    commonChartData() {
      const topTenSkuIds = this.chartData.slice(0, 10)
      const chartHeader = [
        'Month',
        ...topTenSkuIds.map((skuId) => this.skuNames[skuId]),
      ]
      const chartBody = this.getMonths(this.commonChartPeriod).map((month) => [
        month,
      ])

      chartBody.forEach((row) => {
        const [month] = row
        topTenSkuIds.forEach((skuId) => {
          if (
            hasNestedProperties(this.objectUsageData, [
              month,
              this.curProjectId,
              skuId,
            ])
          ) {
            row.push(
              this.getCostByCurrency(
                this.objectUsageData[month][this.curProjectId][skuId]
              )
            )
          } else {
            row.push(0)
          }
        })
      })
      return [chartHeader, ...chartBody]
    },

    donutChartData() {
      const chartHeader = ['sku', 'cost']
      const chartBody = []
      this.chartData.slice(0, this.donutChartCount).forEach((skuId) => {
        if (
          hasNestedProperties(this.objectUsageData, [
            this.preMonth,
            this.curProjectId,
            skuId,
          ])
        ) {
          const cost = this.getCostByCurrency(
            this.objectUsageData[this.preMonth][this.curProjectId][skuId]
          )
          chartBody.push([this.skuNames[skuId], cost >= 0 ? cost : 0])
        } else {
          chartBody.push([this.skuNames[skuId], 0])
        }
      })
      return [chartHeader, ...chartBody]
    },

    tableChartData() {
      const getCost = (month, skuId) => {
        if (
          !hasNestedProperties(this.objectUsageData, [
            month,
            this.curProjectId,
            skuId,
          ])
        )
          return 0
        return this.getCostByCurrency(
          this.objectUsageData[month][this.curProjectId][skuId]
        )
      }
      const getVariance = (cost1, cost2) => {
        const variance = ((cost1 - cost2) / cost2) * 100
        return Number.isFinite(variance) ? variance.toFixed(2) : 0
      }
      const oneMonthAgo = this.getMonthBefore(1)
      const twoMonthAgo = this.getMonthBefore(2)
      const threeMonthAgo = this.getMonthBefore(3)

      const totalData = {
        costOneMonthAgo: 0,
        costTwoMonthAgo: 0,
        costThreeMonthAgo: 0,
      }
      const tableChartData = this.chartData.map((skuId) => {
        const costOneMonthAgo = getCost(oneMonthAgo, skuId)
        const costTwoMonthAgo = getCost(twoMonthAgo, skuId)
        const costThreeMonthAgo = getCost(threeMonthAgo, skuId)

        totalData.costOneMonthAgo += costOneMonthAgo
        totalData.costTwoMonthAgo += costTwoMonthAgo
        totalData.costThreeMonthAgo += costThreeMonthAgo

        return {
          skuName: this.skuNames[skuId],
          oneMonthAgo: { month: oneMonthAgo, cost: costOneMonthAgo },
          twoMonthAgo: { month: twoMonthAgo, cost: costTwoMonthAgo },
          threeMonthAgo: { month: threeMonthAgo, cost: costThreeMonthAgo },
          variance: getVariance(costOneMonthAgo, costTwoMonthAgo),
        }
      })

      totalData.variance = getVariance(
        totalData.costOneMonthAgo,
        totalData.costTwoMonthAgo
      )
      return {
        list: tableChartData,
        total: totalData,
      }
    },

    enabledCredit() {
      return this.creditFilter.filters
        .filter((credit) => this.creditFilter.enabled.includes(credit.id))
        .map((credit) => credit.propName)
    },
  },

  mounted() {
    this.load()
  },

  methods: {
    async load() {
      if (!this.curAccountId) return
      this.accountProject = await this.getAccountProject()
      if (!this.curProjectId) {
        this.$store.commit('curProject', this.getCurProject())
      }
      this.usageData = await this.getUsageData()
      this.objectUsageData = this.getObjectUsageData()
      this.initApplyedSkuIds()
    },

    async getAccountProject() {
      const summaryData = await api.aggregate.summary(
        this.curAccount.id,
        this.preMonth
      )
      return getProject(summaryData, this.userInfo, this.curAccount)
    },

    getCurProject() {
      const curProject = { id: null, name: '' }
      if (this.accountProject.all) {
        curProject.id = this.totalProjectId
      } else if (this.accountProject.list) {
        curProject.id = this.accountProject.list[0].id
        curProject.name = this.accountProject.list[0].name
      }
      return curProject
    },

    getUsageData() {
      return api.usage.monthlyByPeriod(
        this.curAccount.id,
        this.preMonth,
        this.usageDataPeriod
      )
    },

    getObjectUsageData() {
      return this.usageData.reduce((obj, data) => {
        const invoiceMonth = formatDate(data.invoice_month, 'YYYY-MM')
        const projectId = data.project_id === '' ? '__account' : data.project_id

        if (!hasProperty(obj, invoiceMonth)) {
          obj[invoiceMonth] = { [this.totalProjectId]: {} }
        }
        if (!hasProperty(obj[invoiceMonth], projectId)) {
          obj[invoiceMonth][projectId] = {}
          if (!hasProperty(this.projectNames, projectId)) {
            this.projectNames[projectId] =
              projectId === '__account' ? '__account' : data.project_name
          }
        }
        if (!hasProperty(obj[invoiceMonth][projectId], data.sku_id)) {
          obj[invoiceMonth][projectId][data.sku_id] = {}
          obj[invoiceMonth][projectId][data.sku_id].cost = 0
          obj[invoiceMonth][projectId][data.sku_id].cost_in_usd = 0
        }
        if (!hasProperty(obj[invoiceMonth][this.totalProjectId], data.sku_id)) {
          obj[invoiceMonth][this.totalProjectId][data.sku_id] = {}
          obj[invoiceMonth][this.totalProjectId][data.sku_id].cost = 0
          obj[invoiceMonth][this.totalProjectId][data.sku_id].cost_in_usd = 0
          this.skuNames[data.sku_id] = data.sku_name
        }

        obj[invoiceMonth][this.totalProjectId][data.sku_id].cost += data.cost
        obj[invoiceMonth][this.totalProjectId][data.sku_id].cost_in_usd +=
          data.cost_in_usd
        obj[invoiceMonth][projectId][data.sku_id].cost += data.cost
        obj[invoiceMonth][projectId][data.sku_id].cost_in_usd +=
          data.cost_in_usd

        this.enabledCredit.forEach((credit) => {
          if (data[credit] === 0) return
          obj[invoiceMonth][this.totalProjectId][data.sku_id].cost +=
            data[credit]
          obj[invoiceMonth][this.totalProjectId][data.sku_id].cost_in_usd +=
            data[`${credit}_in_usd`]
          obj[invoiceMonth][projectId][data.sku_id].cost += data[credit]
          obj[invoiceMonth][projectId][data.sku_id].cost_in_usd +=
            data[`${credit}_in_usd`]
        })

        return obj
      }, {})
    },

    getMonthBefore(period = 1) {
      return getMonthBefore(this.curMonth, period)
    },

    getMonths(period) {
      const months = []
      for (let i = period; i >= 1; i -= 1) {
        months.push(this.getMonthBefore(i))
      }
      return months
    },

    initApplyedSkuIds() {
      const curProject = [
        ...this.applyedSkuIds.curProject.filter((skuId) =>
          this.skuIds.includes(skuId)
        ),
        ...this.applyedSkuIds.otherProject.filter((skuId) =>
          this.skuIds.includes(skuId)
        ),
      ]
      const otherProject = [
        ...this.applyedSkuIds.curProject.filter(
          (skuId) => !this.skuIds.includes(skuId)
        ),
        ...this.applyedSkuIds.otherProject.filter(
          (skuId) => !this.skuIds.includes(skuId)
        ),
      ]
      this.selectedSkuIds = [...curProject, ...otherProject]
      this.applyedSkuIds = {
        curProject,
        otherProject,
      }
      this.chartData = this.getChartData()
    },
    setApplyedSkuIds() {
      this.$refs.projectToolbar.$el.click()
      this.applyedSkuIds = {
        curProject: this.selectedSkuIds.filter((skuId) =>
          this.skuIds.includes(skuId)
        ),
        otherProject: this.selectedSkuIds.filter(
          (skuId) => !this.skuIds.includes(skuId)
        ),
      }
      this.chartData = this.getChartData()
    },

    getChartData() {
      const lastMonthByUsageData = Object.keys(this.objectUsageData)[0]
      if (this.applyedSkuIds.curProject.length === 0) {
        if (this.applyedSkuIds.otherProject.length === 0) {
          return this.skuIds.sort((sku1, sku2) => {
            const cost1 = hasNestedProperties(
              this.objectUsageData[lastMonthByUsageData],
              [this.curProjectId, sku1]
            )
              ? this.objectUsageData[lastMonthByUsageData][this.curProjectId][
                  sku1
                ].cost
              : 0
            const cost2 = hasNestedProperties(
              this.objectUsageData[lastMonthByUsageData],
              [this.curProjectId, sku2]
            )
              ? this.objectUsageData[lastMonthByUsageData][this.curProjectId][
                  sku2
                ].cost
              : 0
            return cost2 - cost1
          })
        }
        return []
      }
      return this.applyedSkuIds.curProject
    },

    setAllSkuIds() {
      this.selectedSkuIds = this.searchedSkuIds
    },
    setClearSkuIds() {
      this.selectedSkuIds = []
    },

    getCostByCurrency(skuCost) {
      return this.displayCurrency === 'KRW' ? skuCost.cost : skuCost.cost_in_usd
    },
  },
}
</script>

<style lang="scss" scoped>
.md-content {
  margin: 10px 8px 8px 8px;
}

.md-card {
  margin: 8px;
}

.md-card-header {
  height: 50px;
}

.md-switch {
  margin-top: 0;
}

.md-switch-container .md-switch-thumb {
  color: rgba(0, 0, 0, 0.38);
  background-color: rgba(0, 0, 0, 0.38);
}

.md-checkbox {
  margin: auto;
}

.donut-chart-height {
  @media (min-width: 1280px) {
    height: 400px;
  }
  @media (max-width: 1279px) {
    height: 290px;
  }
}

.min-table {
  @media (min-width: 1280px) {
    height: 350px;
  }
}

.main-table {
  @media (min-width: 1280px) {
    margin-top: 0;
  }
  margin-bottom: 0;
}

.total-table {
  margin-top: 0;
  margin-bottom: 0;
  font-weight: bold;
}

.table-height {
  @media (min-width: 1280px) {
    height: 400px;
  }
}

.cost-table {
  ::v-deep .md-table-cell-container,
  ::v-deep .md-table-head-label {
    padding-right: 15px !important;
    padding-left: 15px !important;
  }
}
.card-title {
  font-size: 14px;
  padding-bottom: 0;
}

.md-search-field {
  margin: 6px 0;
  padding: 0;
  min-height: 32px;
  width: 14vw;
}

.search-top {
  padding-left: 0;
}

.sku-search-label {
  top: 6px;
  margin: auto;
}

.search-space-div {
  height: 90px;
}

.sku-apply-button {
  font-size: 12px;
  margin-top: 6px;
  margin-bottom: 6px;
}

.sku-select-button {
  width: 100%;
  margin: auto;
}

.margin-auto {
  margin: auto;
}
</style>
